// Session lifecycle for the signed-request scheme.
//
// On first use the client fetches the public PoW parameters, computes its device
// fingerprint, solves the bootstrap proof-of-work in WASM, and exchanges it at
// /session/init for a short-lived access token, a refresh token, and a
// per-session HMAC signing key. The access token is refreshed shortly before it
// expires; if the refresh token is gone/expired we re-init.
//
// Everything is in memory only — the signing key never touches localStorage, and
// a page reload just pays one bootstrap PoW again (a few hundred ms). Concurrent
// callers share a single in-flight init/refresh.
//
// If the backend layer is disabled (/session/params -> {enabled:false}), this
// returns null and callers fall back to the legacy query-token path, so the
// frontend can ship before the backend is switched on.

import { buildDetailsUrl } from '../api/http';
import { getFingerprint } from './fingerprint';
import { solvePow } from './signer';
import { setQuota } from './quotaState';
import { getTurnstileToken } from './turnstile';

export interface SessionState {
  sid: string;
  accessToken: string;
  refreshToken: string;
  signingKey: string;
  encKey: string; // hex AES-256 key for payload encryption
  powBitsRequest: number;
  accessExpiresAt: number; // epoch ms, already minus a safety margin
}

interface SessionParams {
  enabled: boolean;
  powBitsInit: number;
  powBitsRequest: number;
  initWindowSec: number;
  turnstile: {
    enabled: boolean;
    siteKey: string;
    action: string;
  };
}

const TURNSTILE_DISABLED = { enabled: false, siteKey: '', action: 'session_init' };

const REFRESH_MARGIN_MS = 15_000;

let params: Promise<SessionParams> | null = null;
let state: SessionState | null = null;
let initInFlight: Promise<SessionState> | null = null;
let refreshInFlight: Promise<SessionState> | null = null;

function fetchParams(): Promise<SessionParams> {
  if (!params) {
    params = (async () => {
      try {
        const res = await fetch(buildDetailsUrl('/session/params', {}), { headers: { Accept: 'application/json' } });
        if (!res.ok) return { enabled: false, powBitsInit: 0, powBitsRequest: 0, initWindowSec: 300, turnstile: TURNSTILE_DISABLED };
        const data = (await res.json()) as Partial<SessionParams>;
        const turnstile = data.turnstile;
        return {
          enabled: Boolean(data.enabled),
          powBitsInit: Number(data.powBitsInit ?? 18),
          powBitsRequest: Number(data.powBitsRequest ?? 12),
          initWindowSec: Number(data.initWindowSec ?? 300),
          turnstile: {
            enabled: Boolean(turnstile?.enabled && turnstile.siteKey),
            siteKey: String(turnstile?.siteKey ?? ''),
            action: String(turnstile?.action || 'session_init'),
          },
        };
      } catch {
        // Network error fetching params — assume disabled so the app still works.
        return { enabled: false, powBitsInit: 0, powBitsRequest: 0, initWindowSec: 300, turnstile: TURNSTILE_DISABLED };
      }
    })();
  }
  return params;
}

export async function securityEnabled(): Promise<boolean> {
  return (await fetchParams()).enabled;
}

async function doInit(): Promise<SessionState> {
  const p = await fetchParams();
  const fp = await getFingerprint();
  const turnstileToken = p.turnstile.enabled
    ? await getTurnstileToken(p.turnstile.siteKey, p.turnstile.action)
    : '';
  const bucket = Math.floor(Date.now() / 1000 / p.initWindowSec);
  const pow = await solvePow(`init:${fp}:${bucket}`, p.powBitsInit);

  const res = await fetch(buildDetailsUrl('/session/init', {}), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ fp, pow, ...(turnstileToken ? { turnstileToken } : {}) }),
  });
  if (!res.ok) throw new Error(`session init failed: HTTP ${res.status}`);
  const data = await res.json();
  state = {
    sid: String(data.sid),
    accessToken: String(data.accessToken),
    refreshToken: String(data.refreshToken),
    signingKey: String(data.signingKey),
    encKey: String(data.encKey),
    powBitsRequest: Number(data.powBitsRequest ?? p.powBitsRequest),
    accessExpiresAt: Date.now() + Number(data.accessTtlSec ?? 600) * 1000 - REFRESH_MARGIN_MS,
  };
  if (data.quota) setQuota(data.quota);
  return state;
}

async function doRefresh(current: SessionState): Promise<SessionState> {
  const res = await fetch(buildDetailsUrl('/session/refresh', {}), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ refreshToken: current.refreshToken }),
  });
  if (!res.ok) throw new Error(`session refresh failed: HTTP ${res.status}`);
  const data = await res.json();
  state = {
    ...current,
    sid: String(data.sid ?? current.sid),
    accessToken: String(data.accessToken),
    signingKey: String(data.signingKey ?? current.signingKey),
    encKey: String(data.encKey ?? current.encKey),
    accessExpiresAt: Date.now() + Number(data.accessTtlSec ?? 600) * 1000 - REFRESH_MARGIN_MS,
  };
  return state;
}

function init(): Promise<SessionState> {
  if (!initInFlight) {
    initInFlight = doInit().finally(() => {
      initInFlight = null;
    });
  }
  return initInFlight;
}

function refresh(current: SessionState): Promise<SessionState> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh(current).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * A valid session, or null when the security layer is disabled server-side.
 * Handles bootstrap, refresh-before-expiry, and re-init if refresh fails.
 */
export async function ensureSession(): Promise<SessionState | null> {
  if (!(await securityEnabled())) return null;
  if (state && Date.now() < state.accessExpiresAt) return state;
  if (state) {
    try {
      return await refresh(state);
    } catch {
      state = null; // refresh token gone/expired — fall through to a fresh init
    }
  }
  return init();
}

/** Force a brand-new session (used after an unexpected 401 mid-request). */
export async function reinitSession(): Promise<SessionState | null> {
  if (!(await securityEnabled())) return null;
  state = null;
  return init();
}
